import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import API_URL from "../api/baseURL";

const formatFCFA = (num) =>
  num ? `${Number(num).toLocaleString("fr-CI")} FCFA` : "0 FCFA";

export default function OwnerInvoiceFromQuotationPage() {
  const { id } = useParams(); // This is a *quotationId* from URL
  const navigate = useNavigate();
  const { token } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [taxRate] = useState(18); // Change as needed
  const [applyTax, setApplyTax] = useState(false);

  useEffect(() => {
    if (!id || !token) return;
    setLoading(true);
    fetch(`${API_URL}/workshop-invoices/from-quotation/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (!res.ok) throw new Error("Quotation not found or access denied");
        return res.json();
      })
      .then(data => {
        if (data && data._id && window.location.pathname !== `/owner/invoice/${data._id}`) {
          navigate(`/owner/invoice/${data._id}`, { replace: true });
          return;
        }
        setInvoice(data);
        // If client is company, enable tax
        if (data && data.client && (data.client.isCompany || data.client.type === "company")) {
          setApplyTax(true);
        }
        setError("");
      })
      .catch((err) => {
        setError(err.message || "Failed to load invoice");
        setInvoice(null);
      })
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  if (loading) return <div style={{ color: "white" }}>Loading invoice...</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;
  if (!invoice) return null;

  // Use stored tax from invoice (carried over from quotation)
  const taxAmount = invoice.tax > 0 ? invoice.tax : (applyTax ? Math.round((invoice.clientSubtotal || invoice.subtotal || invoice.total) * taxRate / 100) : 0);
  const storedSubtotal = invoice.clientSubtotal || (invoice.subtotal > 0 ? invoice.subtotal : invoice.total);
  const totalWithTax = invoice.clientTotal || (invoice.tax > 0 ? invoice.total : storedSubtotal + taxAmount);

  return (
    <div style={{
      maxWidth: 900,
      margin: "36px auto",
      background: "#fff",
      color: "#232a3b",
      borderRadius: 10,
      padding: 32,
      boxShadow: "0 6px 32px rgba(0,0,0,0.12)"
    }}>
      {/* Header */}
      <div className="text-2xl font-bold mb-4 text-blue-700">
        Facture / Invoice <span className="text-blue-300">{invoice._id}</span>
      </div>
      <div className="flex justify-between mb-6 text-base">
        <div>
          <b>Workshop:</b> {invoice.workshop?.businessName || "-"}<br />
          <b>Address:</b> {invoice.workshop?.address || "-"}<br />
          <b>Contact:</b> {invoice.workshop?.contactNumber || "-"}
        </div>
        <div>
          <b>Client:</b> {invoice.client?.name || "-"}<br />
          <b>Type:</b> {invoice.client?.type || "-"}<br />
          <b>Contact:</b> {invoice.client?.contactNumber || "-"}
        </div>
        <div>
          <b>Vehicle:</b> {invoice.vehicle?.plateNumber || "-"}<br />
          <b>{invoice.vehicle?.brand} {invoice.vehicle?.model}</b><br />
          <b>Year:</b> {invoice.vehicle?.year || "-"}
        </div>
      </div>
      <table className="min-w-full bg-gray-800 text-xs rounded mb-4">
        <thead>
          <tr>
            <th className="p-2">Reference</th>
            <th className="p-2">Designation</th>
            <th className="p-2">Brand</th>
            <th className="p-2">Qty</th>
            <th className="p-2">Unit Price</th>
            <th className="p-2">Total</th>
          </tr>
        </thead>
        <tbody>
          {invoice.items?.map((item, idx) => (
            <tr key={idx}>
              <td className="p-2">{item.reference || "-"}</td>
              <td className="p-2">{item.designation || item.description || "-"}</td>
              <td className="p-2">{item.brand || "-"}</td>
              <td className="p-2" style={{ textAlign: "center" }}>{item.quantity ?? 0}</td>
              <td className="p-2" style={{ textAlign: "right" }}>{formatFCFA(item.clientPrice ?? item.unitPrice ?? item.price ?? 0)}</td>
              <td className="p-2" style={{ textAlign: "right" }}>
                {formatFCFA((item.clientPrice ?? item.unitPrice ?? item.price ?? 0) * (item.quantity ?? 0))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {/* Totals */}
      {taxAmount > 0 ? (
        <>
          <div className="font-bold text-blue-500 text-lg mt-3">
            Sous-total HT : {formatFCFA(storedSubtotal)}
          </div>
          <div className="font-bold text-yellow-400 text-base mt-1">
            TVA (18%) : {formatFCFA(taxAmount)}
          </div>
          <div className="font-bold text-green-500 text-xl mt-1">
            Total TTC : {formatFCFA(totalWithTax)}
          </div>
        </>
      ) : (
        <div className="font-bold text-green-500 text-xl mt-3">
          Total : {formatFCFA(invoice.clientTotal || invoice.total)}
        </div>
      )}
    </div>
  );
}
